import { FC, useState } from 'react';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useForm } from '@/libs/antd/form';
import {
	Button,
	Col,
	Form,
	Select,
	Row,
	Space,
	Upload,
	Divider,
	Alert,
	Result,
	Spin,
} from 'antd';
import FleetTitle from '@/libs/antd/components/Title';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import { FilePayload } from '@/libs/fleet-api/services/types';
import {
	ArrowLeftOutlined,
	DeleteOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import rule from '@/libs/antd/rules';
import { useApp } from '@/App';
import {
	BankDocument,
	generateOCR,
	OCRGeneratePayload,
} from '@/libs/fleet-api/services/ocr';
import FleetModal from '@/libs/antd/components/Modal';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import OCRBankStatementSampleModal, {
	useOCRBankStatementSampleModal,
} from './modals/OCRBankStatementSampleModal';

type OCRGenerateForm = Omit<OCRGeneratePayload, 'documents'> & {
	documents: BankDocument[];
};

const OCRCreate: FC<{ customerId: string; bankOptions: MasterData[] }> = ({
	customerId,
	bankOptions,
}) => {
	const { navigate, notification } = useApp();
	const { form, value, fieldName } = useForm<OCRGenerateForm>();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});
	const submitGenerateOCRMutation = useMutation({
		mutationFn: generateOCR,
	});

	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const { openPreviewModal } = useModals();
	const OCRBankStatementModal = useOCRBankStatementSampleModal();

	const newDocument: FilePayload = {
		id: '',
		name: '',
		path: '',
	};
	const newBankDocument: BankDocument = {
		bankId: '',
		documents: [newDocument],
	};

	const bankOptionsSelect = bankOptions.map((bank) => ({
		label: bank.value,
		value: bank.id,
		data: bank,
	}));
	const [selectedBanks, setSelectedBanks] = useState<number[]>([]);
	const filteredBankOptions = bankOptionsSelect.map((option) => ({
		...option,
		disabled: selectedBanks.includes(option.value),
	}));

	const handleGenerateOcr = async () => {
		console.log('generate ocr');
		try {
			setShowConfirmModal(false);
			const values = form.getFieldsValue();
			console.log(values);
			const ocrGeneratePayload: OCRGeneratePayload = {
				customerId,
				sessionName: `${customerId}-${Date.now()}`,
				documents: values.documents
					.filter((bank) => bank.bankId)
					.map((bank) => ({
						bankId: bank.bankId,
						documents: bank.documents?.filter((doc) => doc.name) || [],
					})),
			};
			await submitGenerateOCRMutation.mutateAsync(ocrGeneratePayload);
			notification.success({
				message: 'Success',
				description: 'Generate OCR success',
			});
			navigate('OCRPage', { customerId });
		} catch {
			notification.error({
				message: 'Failed',
				description: 'Generate OCR failed',
			});
		}
	};

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<div
				style={{
					display: 'flex',
					gap: 10,
				}}
			>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => {
							navigate('OCRPage', { customerId });
						}}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>Add Rekening</FleetTitle>
				</div>
			</div>

			<Row>
				<Col flex="auto" />
				<Col flex="none">
					<Button
						style={{
							borderRadius: 50,
							fontSize: 12,
							height: 24,
							fontWeight: 700,
							borderColor: '#1557BC',
							color: '#1557BC',
						}}
						icon={'+'}
						onClick={() => {
							form.setFieldValue(fieldName('documents'), [
								...(value?.documents ?? []),
								newBankDocument,
							]);
						}}
					>
						Add Another
					</Button>
				</Col>
			</Row>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{
					style: {
						width: 200,
					},
				}}
				onSubmitCapture={async () => {
					const isFormValid = await form.validateFields();
					if (isFormValid) {
						setShowConfirmModal(true);
					}
				}}
			>
				<Form.Item
					name={fieldName(['customerId'])}
					initialValue={customerId}
					noStyle
				/>
				<Form.Item
					name={fieldName(['documents'])}
					initialValue={[newBankDocument]}
					noStyle
				/>
				<Spin spinning={submitGenerateOCRMutation.isPending}>
					<table
						style={{
							border: '1px solid #ddd',
							width: '100%',
							borderCollapse: 'collapse',
							background: '#ffffff',
						}}
					>
						<thead>
							<tr style={{ background: '#FFF8DD' }}>
								<th
									style={{
										border: '1px solid #ddd',
										padding: '8px',
										width: '50%',
									}}
								>
									Nama Bank
								</th>
								<th style={{ border: '1px solid #ddd', padding: '8px' }}>
									Upload File
								</th>
							</tr>
						</thead>
						<tbody>
							{value?.documents?.map((bank, bankIndex) => (
								<tr key={bankIndex}>
									<td
										style={{
											border: '1px solid #ddd',
											padding: '8px',
											verticalAlign: 'top',
										}}
									>
										<Form.Item
											name={`bankId${bankIndex}`}
											initialValue={value.documents?.[bankIndex].bankId}
											rules={[rule.required()]}
											getValueProps={(value) => {
												if (!value) return {};
												return {
													value: value.id,
												};
											}}
										>
											<Select
												options={
													filteredBankOptions?.length ? filteredBankOptions : []
												}
												onChange={(value: string) => {
													form.setFieldValue(
														fieldName(['documents', bankIndex, 'bankId']),
														value,
													);
													setSelectedBanks([]);
												}}
											/>
										</Form.Item>
									</td>
									<td style={{ border: '1px solid #ddd', padding: '8px' }}>
										{bank.documents?.map((doc, docIndex) => (
											<>
												<div
													key={docIndex}
													style={{ display: 'flex', alignItems: 'center' }} // Changed alignItems to 'center'
												>
													<Button
														type="text"
														style={{ marginRight: '10px' }}
														onClick={() => {
															if (bank.documents && bank.documents.length > 1) {
																// Remove bank documents file values
																const removeFileButton = document.querySelector(
																	'button[title="Remove file"]:first-of-type',
																) as HTMLButtonElement;
																if (removeFileButton) {
																	removeFileButton.click();
																}

																// Remove selected bank document from bank documents
																const updatedDocuments = value.documents?.[
																	bankIndex
																].documents?.filter(
																	(_, index) => index !== docIndex,
																);
																form.setFieldValue(
																	fieldName([
																		'documents',
																		bankIndex,
																		'documents',
																	]),
																	updatedDocuments,
																);
															} else {
																const updatedBanks = value.documents?.filter(
																	(_, index) => index !== bankIndex,
																);
																form.setFieldValue(
																	fieldName('documents'),
																	updatedBanks,
																);
															}
														}}
													>
														<DeleteOutlined />
													</Button>
													{!doc.id ? (
														<Form.Item
															name={`uploader${bankIndex}${docIndex}`}
															rules={[rule.required()]}
														>
															<Upload.Dragger
																style={{ width: 260 }}
																listType="picture"
																accept=".pdf,"
																maxCount={1}
																beforeUpload={() => false}
																onChange={async ({ file }) => {
																	if (file.status === 'removed') {
																		form.setFieldValue(
																			fieldName([
																				'documents',
																				bankIndex,
																				'documents',
																				docIndex,
																			]),
																			newDocument,
																		);
																		return;
																	}

																	const fileid = Date.now().toString();
																	const filepath = `/customer/${customerId}/ocr/generate`;

																	try {
																		const url =
																			await uploadMutation.mutateAsync({
																				file: file as unknown as File,
																				fileid,
																				filepath,
																			});
																		form.setFieldValue(
																			fieldName([
																				'documents',
																				bankIndex,
																				'documents',
																				docIndex,
																			]),
																			{
																				format: 'pdf',
																				id: fileid,
																				name: file.name,
																				path: filepath,
																				url,
																			},
																		);
																	} catch {
																		form.setFieldValue(
																			fieldName([
																				'documents',
																				bankIndex,
																				'documents',
																				docIndex,
																			]),
																			null,
																		);
																	}
																}}
															>
																<UploadOutlined
																	style={{
																		fontSize: 25,
																		color: '#BABABA',
																		display: 'block',
																		margin: '10px',
																	}}
																/>
															</Upload.Dragger>
														</Form.Item>
													) : (
														<a
															onClick={(e) => {
																e.preventDefault();
																if (!doc.id) return;
																openPreviewModal(doc as FleetFile);
															}}
														>
															{doc.name}
														</a>
													)}
												</div>
												<Divider />
											</>
										))}

										<Button
											style={{
												borderRadius: 50,
												fontSize: 12,
												height: 24,
												fontWeight: 700,
												borderColor: '#1557BC',
												color: '#1557BC',
											}}
											icon={'+'}
											onClick={() => {
												form.setFieldValue(
													fieldName(['documents', bankIndex, 'documents']),
													[
														...(value?.documents?.[bankIndex]?.documents ?? []),
														newDocument,
													],
												);
											}}
										>
											Add Another Document
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</Spin>
				<Alert
					message={
						<>
							Sebelum mengunggah file rekening untuk proses OCR, mohon untuk
							memeriksa terlebih dahulu tampilan file dari masing-masing bank.
							Anda dapat melihat contoh tampilan file{' '}
							<strong>
								<a
									onClick={(e) => {
										e.preventDefault();
										OCRBankStatementModal.open({
											customerId,
											bankOptions,
										});
									}}
								>
									di sini
								</a>
							</strong>
						</>
					}
					type="info"
					showIcon
					style={{ marginTop: '20px' }}
				/>
				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 20,
					}}
					disabled={submitGenerateOCRMutation.isPending}
					loading={submitGenerateOCRMutation.isPending}
				>
					Generate OCR
				</Button>
			</Form>
			<FleetModal
				open={showConfirmModal}
				styles={{ footer: { padding: 0 } }}
				okText="YA"
				cancelText="TIDAK"
				onCancel={() => setShowConfirmModal(false)}
				onOk={() => handleGenerateOcr()}
			>
				<Result
					title="Apakah Anda yakin ingin generate OCR?"
					subTitle={
						<p style={{ color: '#BBBBBB' }}>
							Pastikan dokumen yang diunggah jelas dan sesuai
						</p>
					}
				></Result>
			</FleetModal>
			<OCRBankStatementSampleModal {...OCRBankStatementModal} />
		</Space>
	);
};

export default OCRCreate;
